import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Field, reduxForm, formValueSelector, change } from 'redux-form';
import { FormattedMessage } from 'react-intl';
import messages from '../../../text/messages';
import selectOptions from '../../../text/selectOptions';
import { RadioField, SelectField } from './../../../components/fields';
import { required, errorMessages } from '../../../utilities/validation';
import Modal from '../../../components/modal';
import { AddressForm } from '../../../components/forms';
import WizardButtons from '../../../components/wizard/navigator/buttons';
import { getAddresses, saveAddress } from '../../../reducers/addresses';
import { dateToFloatingString } from '../../../utilities/dateTime';

import './steps.css';

class PositionTheWorkLocationStep extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            modal: { open: false, type: undefined }
        };

        this.getLocationTypes = this.getLocationTypes.bind(this);

        // modal controls
        this.onOpen = this.onOpen.bind(this);
        this.onClose = this.onClose.bind(this);
    }

    componentDidMount() {
        const { role, addresses, getAddresses } = this.props;
        if (!addresses) getAddresses(role);
    }

    getLocationTypes() {
        const { work_location_type } = this.props;
        return [
            {
                value: 'onsite',
                label: (
                    <div className="location-type-option">
                        <i className="material-icons">{work_location_type === 'onsite' ? 'radio_button_checked' : 'radio_button_unchecked'}</i>
                        <FormattedMessage {...messages.onsite} tagName={'span'} />
                    </div>
                )
            },
            {
                value: 'offsite',
                label: (
                    <div className="location-type-option">
                        <i className="material-icons">{work_location_type === 'offsite' ? 'radio_button_checked' : 'radio_button_unchecked'}</i>
                        <FormattedMessage {...messages.offsite} tagName={'span'} />
                    </div>
                )
            }
        ];
    }

    getTimezones(country) {
        return selectOptions.timezones;
    }

    onOpen(type) {
        this.setState({ modal: { open: true, type } });
    }

    onClose() {
        this.setState({ modal: { open: false } });
    }

    render() {
        const { modal } = this.state;
        const {
            handleSubmit,
            cancel,
            saveAddress,
            getAddresses,
            addresses,
            work_location_type,
            back,
            submitting,
            role,
            changeField,
            countriesOptions
        } = this.props;
        if (!addresses) {
            return null;
        }

        let addressOptions = addresses.map(a => ({ value: a.id, label: `${a.name} (${a.street} ${a.city} ${a.state} ${a.country})` }));

        return (
            <div>
                <form onSubmit={handleSubmit}>
                    <div className="row">
                        <div className="col-xs-12 col-sm-3">
                            <Field name="country" component={SelectField} options={countriesOptions} validate={required} placeholder={messages.country} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-xs-12">
                            <div className="location-type-choice">
                                <Field
                                    name={'work_location_type'}
                                    placeholder={messages.whatIsPrimary}
                                    component={RadioField}
                                    options={this.getLocationTypes()}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        {work_location_type === 'onsite' && (
                            <div className="col-sm-6 col-xs-12">
                                <div className="row">
                                    <div className="col-xs-9 col-sm-10">
                                        <Field
                                            name="work_location_onsite_id"
                                            component={SelectField}
                                            options={addressOptions}
                                            placeholder={messages.workLocation}
                                            disabled={work_location_type !== 'onsite'}
                                        />
                                    </div>
                                    <div className="col-xs-3 col-sm-2">
                                        <button type="button" className="field-inline-button" onClick={() => this.onOpen('address')}>
                                            <FormattedMessage {...messages.add} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="col-xs-12 col-sm-6">
                            <Field
                                name="work_timezone"
                                component={SelectField}
                                validate={required}
                                options={selectOptions.timezones}
                                placeholder={messages.workTimezone}
                            />
                        </div>
                    </div>

                    <WizardButtons disabled={submitting} back={{ action: back }} cancel={{ action: cancel }} />
                </form>

                <Modal
                    open={modal.open}
                    onClose={this.onClose}
                    managedClose
                    size="col-sm-8 col-sm-offset-2 col-xs-12"
                    title={<FormattedMessage {...(modal.type === 'address' ? messages.addAddress : messages.addUser)} />}
                >
                    <div className="row">
                        <AddressForm
                            onSubmit={values =>
                                saveAddress(values, result =>
                                    getAddresses(role, () => {
                                        changeField('position-wizard', 'work_location_onsite_id', result.id);
                                        this.onClose();
                                    })
                                )
                            }
                            initialValues={{ ref_id: role.id, ref_type: role.type }}
                            cancel={this.onClose}
                            form="address-form-new"
                        />
                    </div>
                </Modal>
            </div>
        );
    }
}

PositionTheWorkLocationStep = reduxForm({
    form: 'position-wizard',
    destroyOnUnmount: false,
    enableReinitialize: true,
    forceUnregisterOnUnmount: true,
    validate: values => {
        const errors = {};
        if (values.end_date && values.start_date && values.end_date < values.start_date) {
            errors.end_date = errorMessages.minValue(dateToFloatingString(values.start_date.add(1, 'days'), 'MM/DD/YYYY'));
        }
        return errors;
    }
})(PositionTheWorkLocationStep);

const selector = formValueSelector('position-wizard');
const mapStateToProps = (state, props) => {
    const countriesOptions = Object.entries(props.countries || state.identity.details.countries || {}).reduce((a, [key, value]) => {
        if (value === 'active') {
            a.push({ value: key, label: key });
        }
        return a;
    }, []);

    return {
        work_location_type: selector(state, 'work_location_type'),
        work_location_onsite_id: selector(state, 'work_location_onsite_id'),
        countriesOptions,
        role: state.identity.role,
        userDetails: state.identity.details,
        addresses: state.addresses.list,
        usersRole: state.users.listRole,
        initialValues: {
            work_location_onsite_id: state.addresses.list && state.addresses.list.length > 0 ? state.addresses.list[0].id : undefined,
            ...props.initialValues,
            country: countriesOptions.length === 1 ? countriesOptions[0].value : undefined,
            ...props.position,
            ...(props.positionId && state.positions[props.positionId] ? state.positions[props.positionId] : {})
        }
    };
};

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            getAddresses: getAddresses,
            saveAddress: saveAddress,
            changeField: change
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(PositionTheWorkLocationStep);



// WEBPACK FOOTER //
// ./src/pages/position/steps/positionTheWorkLocation.jsx